import { type SEOHandle } from '@nasa-gcn/remix-seo'
import {
	json,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Link, useLoaderData } from '@remix-run/react'
import { useState } from 'react'
import { Input } from '#app/components/ui/input'
import { authenticator } from '#app/utils/auth.server'
import { prisma } from '#app/utils/db.server'

type LeaderboardItem = {
	id: string
	handle: string
	imageUrl: string | null
	plotsCount: number
	treesPlanted: number
	rank: number
}

type LoaderData = {
	leaderboard: LeaderboardItem[]
	currentUserRank: LeaderboardItem | null
}

export const handle: SEOHandle = {
	getSitemapEntries: () => [{ route: '/leaderboard' }],
}

export async function loader({ request }: LoaderFunctionArgs) {
	const session = await authenticator.isAuthenticated(request)
	const currentUserId = session?.id ?? null

	// Query all users who have either plots or donations
	const users = await prisma.user.findMany({
		where: {
			OR: [{ plots: { some: {} } }, { donations: { some: {} } }],
		},
		select: {
			id: true,
			handle: true,
			imageUrl: true,
			plots: {
				select: { id: true },
			},
			donations: {
				select: {
					amount: true,
					createdAt: true,
				},
			},
		},
	})

	const list: LeaderboardItem[] = users.map(user => {
		const treesPlantedFromDonations = user.donations.reduce((acc, donation) => {
			const divideBy = donation.createdAt < new Date('2024-04-22') ? 25 : 35
			return acc + donation.amount / divideBy
		}, 0)

		return {
			id: user.id,
			handle: user.handle,
			imageUrl: user.imageUrl,
			plotsCount: user.plots.length,
			treesPlanted: Math.round(treesPlantedFromDonations),
			rank: 0,
		}
	})

	// Sort by treesPlanted DESC, then plotsCount DESC
	list.sort((a, b) => {
		if (b.treesPlanted !== a.treesPlanted) {
			return b.treesPlanted - a.treesPlanted
		}
		return b.plotsCount - a.plotsCount
	})

	// Assign rank
	const leaderboard = list.map((item, index) => ({
		...item,
		rank: index + 1,
	}))

	const currentUserRank = currentUserId
		? leaderboard.find(item => item.id === currentUserId) ?? null
		: null

	return json<LoaderData>({
		leaderboard,
		currentUserRank,
	})
}

export const meta: MetaFunction = () => [
	{ title: 'Community impact | Erda' },
	{
		name: 'description',
		content:
			'Explore the Erda community and estimated trees funded through digital place sponsorships.',
	},
]
export default function Leaderboard() {
	const { leaderboard, currentUserRank } = useLoaderData<typeof loader>()
	const [query, setQuery] = useState('')
	const filtered = leaderboard.filter(item =>
		item.handle.toLowerCase()
			.includes(query.trim().toLowerCase()),
	)
	return (
		<article className="panel-copy">
			<h1>A growing contribution.</h1>
			<p>People supporting tree planting through Erda.</p>
			<label className="block text-sm font-medium" htmlFor="community-search">
				Find a person
			</label>
			<Input
				id="community-search"
				type="search"
				value={query}
				onChange={event => setQuery(event.target.value)}
				placeholder="Handle"
			/>
			<table className="w-full table-fixed text-sm">
				<caption className="sr-only">
					Community ranked by estimated trees funded
				</caption>
				<thead>
					<tr className="border-b text-left text-xs text-muted-foreground">
						<th className="w-14 py-3 pr-3">Rank</th>
						<th className="px-2">Person</th>
						<th className="w-20 text-right">Trees funded</th>
					</tr>
				</thead>
				<tbody>
					{filtered.map(item => (
						<tr key={item.id} className="border-b">
							<td className="py-4 tabular-nums">{item.rank}</td>
							<td className="px-2 py-4">
								<Link to={`/${item.handle}`} className="font-medium">
									@{item.handle}
								</Link>
								{currentUserRank?.id === item.id && (
									<span className="ml-2 text-xs text-muted-foreground">
										You
									</span>
								)}
								<span className="block text-xs text-muted-foreground">
									{item.plotsCount}{' '}
									{item.plotsCount === 1 ? 'place' : 'places'}
								</span>
							</td>
							<td className="text-right tabular-nums">
								{item.treesPlanted.toLocaleString()}
							</td>
						</tr>
					))}
				</tbody>
			</table>
			{!filtered.length && (
				<p>
					{query.trim()
						? 'No matching people. Try another handle.'
						: 'No contributions to show yet.'}
				</p>
			)}
			<p className="text-sm leading-relaxed">
				Tree totals are estimates based on recorded donations.{' '}
				<Link to="/tree-planting">How we count them</Link>
			</p>
			<Link to="/">Choose your own place</Link>
		</article>
	)
}
